/**
 *  All api related to coupon tokens
 */

import _ from 'lodash'
import { CancelToken } from 'axios'
import { OmnitechApi } from '../../api'
import { cancelRequest } from '../../helpers'
import { isErrorExist, convertResponseToErrorObject } from '../../omnitech-error'

export function fetchCouponTokens(options = {}) {
  const { page = 1, ...others } = options

  const params = {
    page,
    ...others
  }

  const source = CancelToken.source()
  cancelRequest.add(
    'fetchCouponTokens',
    () => source.cancel('fetchCouponTokens: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: 'coupon_tokens',
      method: 'get',
      params,
      cancelToken: source.token,
      arrayFormat: 'brackets',
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      couponTokens: _.get(data, 'couponTokens', {}),
      next: _.get(data, 'couponTokensPagination.nextPage') > page
        ? () =>
          fetchCouponTokens({
            page: page + 1,
            ...others
          })
        : null,
      all: page === 1 && _.get(data, 'couponTokensPagination.nextPage') > 1
        ? () => Promise.all(
          _.times(
            _.get(data, 'couponTokensPagination.totalPages') - 1,
            (index) => fetchCouponTokens({
              pageCountless: true,
              page: index + 2,
              ...others
            }),
          ),
        )
        : null,
      count: _.get(data, 'couponTokensPagination.totalCount')
    }))
}

export function fetchCouponToken({ id, ...params }) {
  const source = CancelToken.source()
  cancelRequest.add(
    'fetchCouponToken',
    () => source.cancel('fetchCouponToken: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `coupon_tokens/${id}`,
      method: 'get',
      params,
      cancelToken: source.token,
      arrayFormat: 'brackets',
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      couponToken: _.get(data, 'couponToken', {})
    }))
}

export function fetchCouponTokenByToken({ token, ...params }) {
  const source = CancelToken.source()
  cancelRequest.add(
    'fetchCouponTokenByToken',
    () => source.cancel('fetchCouponTokenByToken: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `coupon_tokens/by_token/${token}`,
      method: 'get',
      params,
      cancelToken: source.token,
      arrayFormat: 'brackets',
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      couponToken: _.get(data, 'couponToken', {})
    }))
}

export function fetchCouponTokenByQrCode({ qrCodeValue, ...params }) {
  const source = CancelToken.source();
  cancelRequest.add("fetchCouponTokenByQrCode", () =>
    source.cancel("fetchCouponTokenByQrCode: Operation cancelled by user")
  );
  return OmnitechApi.request({
    endpoint: `coupon_tokens/show_by_qr_code/${qrCodeValue}`,
    method: "get",
    params,
    cancelToken: source.token,
    arrayFormat: "brackets"
  })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject();
      }
      throw convertResponseToErrorObject(_.get(error, "response.data"), {
        useResponseMessage: true
      });
    })
    .then(response => {
      if (isErrorExist(response.data))
        throw convertResponseToErrorObject(response.data, {
          useResponseMessage: true
        });
      return response.data;
    })
    .then(({ data }) => ({
      couponToken: _.get(data, "couponToken", {})
    }));
}

export function fetchCouponTokenTransferTransactions(options = {}) {
  const { page = 1, ...others } = options;
  const params = {
    transferTypes: ["user_transfer"].join(","),
    page,
    pageSize: 25,
    pageCountless: true,
    sortBy: "transfer_at_desc",
    ...others
  };

  const source = CancelToken.source();
  cancelRequest.add("fetchCouponTokenTransferTransactions", () =>
    source.cancel("fetchCouponTokenTransferTransactions: Operation cancelled by user")
  );

  return OmnitechApi.request({
    endpoint: "coupon_token_transfer_transactions",
    method: "get",
    params,
    cancelToken: source.token,
    arrayFormat: "brackets"
  })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject();
      }
      throw convertResponseToErrorObject(_.get(error, "response.data"), {
        useResponseMessage: true
      });
    })
    .then(response => {
      if (isErrorExist(response.data))
        throw convertResponseToErrorObject(response.data, {
          useResponseMessage: true
        });
      return response.data;
    })
    .then(({ data }) => ({
      couponTokenTransferTransactions: _.get(data, "couponTokenTransferTransactions", {}),
      next:
        _.get(data, "couponTokenTransferTransactionsPagination.nextPage") > page
          ? () =>
              fetchCouponTokenTransferTransactions({
                page: page + 1,
                ...others
              })
          : null,
      count: _.get(data, "couponTokenTransferTransactionsPagination.totalCount")
    }))
}

export function createCouponToken({ couponID, data = {}, ...others }) {
  const params = {
    ...others
  }
  const source = CancelToken.source()
  cancelRequest.add(
    'createCouponToken',
    () => source.cancel('createCouponToken: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `coupons/${couponID}/coupon_tokens/`,
      method: 'post',
      data: { data },
      params,
      cancelToken: source.token,
      arrayFormat: 'brackets',
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      couponToken: _.get(data, 'couponToken', {})
    }))
}

export function createCouponTokenTransfer({
  id, // couponToken.id
  data = {},
  ...others
}) {
  const params = {
    ...others
  }
  const source = CancelToken.source()
  cancelRequest.add(
    'createCouponTokenTransfer',
    () => source.cancel('createCouponTokenTransfer: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `coupon_tokens/${id}/transfer`,
      method: 'post',
      data: { data },
      params,
      cancelToken: source.token,
      arrayFormat: 'brackets',
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      couponToken: _.get(data, 'couponToken', {})
    }))
}

export function createCouponTokenByTokenTransfer({ token, data = {}, ...others }) {
  const params = {
    ...others
  }
  const source = CancelToken.source()
  cancelRequest.add(
    'createCouponTokenByTokenTransfer',
    () => source.cancel('createCouponTokenByTokenTransfer: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `coupon_tokens/by_token/${token}/transfer`,
      method: 'post',
      data: { data },
      params,
      cancelToken: source.token,
      arrayFormat: 'brackets',
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      couponToken: _.get(data, 'couponToken', {})
    }))
}

export function createCouponTokenShare({
  id, // couponToken.id
  data = {},
  ...others
}) {
  const params = {
    ...others
  }
  const source = CancelToken.source()
  cancelRequest.add(
    'createCouponTokenShare',
    () => source.cancel('createCouponTokenShare: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `coupon_tokens/${id}/mark_shared`,
      method: 'post',
      data: { data },
      params,
      cancelToken: source.token,
      arrayFormat: 'brackets',
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      couponToken: _.get(data, 'couponToken', {})
    }))
}
